// =====================================================================
// TimeInput - input H:MM cu 2 casete separate (Q2 - Faza 3.1)
// =====================================================================
// Folosit pentru input ore in Timesheets (vedere grid saptamanala) si
// peste tot unde operatorul/admin tasteaza durata in format ora:minut.
//
// Props:
//   value         - durata in minute (number, ex: 456 = 7h36)
//   onChange(min) - callback cu noua durata in minute (la blur)
//   onCommit(min) - alias pentru onChange (semantic)
//   disabled      - boolean
//   maxHours      - default 16, ALINIAT pe backend:
//                   timesheet.service.js -> MAX_DAY_MINUTES = 16*60
//                   ("Q18; élevé de 11h->16h le 12/08/2026, demande Silviu").
//                   Frontend-ul ramasese la 11 si taia TACUT: o tura de 12h,
//                   acceptata de server, se salva ca 11h fara niciun avertisment.
//                   Nimeni nu transmite prop-ul explicit => o singura limita peste tot.
//   compact       - boolean (varianta mai mica pentru celule grid)
//   ariaLabel     - string pt accessibility
//   liveUpdate    - boolean (default false) - emite si la fiecare tasta, nu doar la blur.
//                   DE FOLOSIT IN FORMULARE (modale cu buton Enregistrer).
//                   NU in grila: acolo onChange salveaza prin API => ar da 1 request/tasta.
//
// Comportament:
//   - 2 input-uri text (numeric only): ore [0..maxHours], minute [00..59]
//   - Edit liber in timpul typing-ului (local state)
//   - La onBlur: clamp + emit valoare finala la parent
//   - Cu liveUpdate: emite si la fiecare schimbare, ca parintele sa aiba mereu
//     valoarea curenta chiar daca utilizatorul apasa "Enregistrer" fara sa iasa
//     din caseta de minute (bug 22-09-2026: minutele se pierdeau, linia intra cu :00)
//   - Focus -> select all (inlocuire rapida)
//   - 0 min total = afiseaza 0:00 (gri pal)
//   - Pad minute: 36 -> '36', 6 -> '06'
//
// Helpers exportate:
//   formatMinutes(min) -> 'H:MM'
//   parseHM(str)       -> minute (parseaza 'H:MM' sau 'H,MM' sau 'H.MM')
//   STANDARD_DAY = 456 (7h36)
// =====================================================================

import { useState, useEffect, useRef } from 'react';

const STANDARD_DAY_MINUTES = 7 * 60 + 36; // 456 = 7h36
export const STANDARD_DAY = STANDARD_DAY_MINUTES;

/** Convert total minute -> { h, m } (clamp negativ la 0). */
function splitMinutes(min) {
  const safe = Math.max(0, Math.floor(Number(min) || 0));
  return { h: Math.floor(safe / 60), m: safe % 60 };
}

/** Convert ore + minute -> total minute. */
function joinMinutes(h, m) {
  const hh = parseInt(h, 10) || 0;
  const mm = parseInt(m, 10) || 0;
  return Math.max(0, hh * 60 + mm);
}

/**
 * Helper public: format duration (minute) -> 'H:MM' string.
 * Folosit pentru afisaj read-only (totaluri, header celule).
 */
export function formatMinutes(min) {
  const { h, m } = splitMinutes(min);
  return `${h}:${String(m).padStart(2, '0')}`;
}

/**
 * Helper public: parse 'H:MM' / 'H,MM' / 'H.MM' / 'HMM' -> minute.
 * Tolerant la separatori multipli (Q2 - input rapid).
 *   '7:36' -> 456, '7,36' -> 456, '7.36' -> 456, '7h36' -> 456
 *   '90' -> 90 (90 min), '1:30' -> 90
 */
export function parseHM(str) {
  if (str == null) return 0;
  const s = String(str).trim().replace(/\s+/g, '');
  if (!s) return 0;
  // Cu separator
  const m = s.match(/^(\d{1,2})[\:\.\,h](\d{1,2})$/i);
  if (m) return joinMinutes(m[1], m[2]);
  // Doar numere -> minute totale
  const n = parseInt(s, 10);
  return Number.isFinite(n) ? Math.max(0, n) : 0;
}

export default function TimeInput({
  value = 0,
  onChange,
  onCommit,
  disabled = false,
  maxHours = 16,
  compact = false,
  ariaLabel = 'Duree',
  liveUpdate = false,
}) {
  // Local state pentru typing fluent
  const initial = splitMinutes(value);
  const [h, setH] = useState(String(initial.h));
  const [m, setM] = useState(String(initial.m).padStart(2, '0'));
  // true cat timp utilizatorul are focus in casete (doar pentru liveUpdate)
  const editingRef = useRef(false);
  // Escape a facut deja commit-ul de revenire -> blur-ul care urmeaza nu mai comite
  // (handleBlur ar folosi h/m din closure = exact cifrele pe care Escape le anuleaza)
  const skipBlurCommitRef = useRef(false);
  // 02-10-2026 — valoarea VIE a celor doua casete.
  // `h` si `m` din corpul componentei sunt fotografia randarii curente. Cand ora
  // si minutele se schimba AMANDOUA inainte ca React sa re-randeze (automatizare,
  // lipire, doua evenimente in acelasi lot), handlerul care vine al doilea citea
  // fotografia VECHE a casetei vecine si trimitea in sus o valoare amputata:
  // minute 20 apoi ora 1 -> emitLive(1, m=STALE "00") -> 1:00, minutele pierdute.
  // Nu apare la tastat de om (intre doua taste se randeaza), dar NOI encodam
  // programatic. Referintele de mai jos sunt scrise sincron, deci mereu actuale.
  const hRef = useRef(String(initial.h));
  const mRef = useRef(String(initial.m).padStart(2, '0'));
  const setHBoth = (v) => { hRef.current = v; setH(v); };
  const setMBoth = (v) => { mRef.current = v; setM(v); };

  // Sync extern -> intern cand value se schimba din parent.
  // In modul liveUpdate parintele ne da inapoi exact ce tocmai am emis; daca am
  // resincroniza in timpul tastarii am strica cifrele ('3' -> 0:03 -> afiseaza '03').
  useEffect(() => {
    if (liveUpdate && editingRef.current) return;
    const split = splitMinutes(value);
    setHBoth(String(split.h));
    setMBoth(String(split.m).padStart(2, '0'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, liveUpdate]);

  const isZero = (parseInt(h, 10) || 0) === 0 && (parseInt(m, 10) || 0) === 0;

  const inputStyle = {
    width: compact ? 28 : 38,
    padding: compact ? '2px 3px' : '4px 6px',
    fontSize: compact ? 12 : 14,
    textAlign: 'center',
    border: '1px solid #cbd5e1',
    borderRadius: 4,
    fontFamily: 'ui-monospace, "SF Mono", Menlo, Consolas, monospace',
    background: disabled ? '#f1f5f9' : '#ffffff',
    color: disabled ? '#64748b' : (isZero ? '#94a3b8' : '#0f172a'),
    outline: 'none',
    boxSizing: 'border-box',
  };

  const sepStyle = {
    margin: '0 2px',
    color: isZero ? '#94a3b8' : '#475569',
    fontWeight: 700,
    fontFamily: 'ui-monospace, monospace',
    userSelect: 'none',
  };

  /** Clamp ore+minute la limitele componentei -> total minute. */
  function clampTotal(nextH, nextM) {
    let cleanH = parseInt(nextH, 10);
    let cleanM = parseInt(nextM, 10);
    if (Number.isNaN(cleanH)) cleanH = 0;
    if (Number.isNaN(cleanM)) cleanM = 0;
    cleanH = Math.max(0, Math.min(maxHours, cleanH));
    cleanM = Math.max(0, Math.min(59, cleanM));
    return { cleanH, cleanM, total: joinMinutes(cleanH, cleanM) };
  }

  function commit(nextH, nextM) {
    const { cleanH, cleanM, total } = clampTotal(nextH, nextM);
    setHBoth(String(cleanH));
    setMBoth(String(cleanM).padStart(2, '0'));
    if (onChange) onChange(total);
    if (onCommit) onCommit(total);
  }

  /**
   * Emit la fiecare tasta, DOAR in modul liveUpdate. Nu normalizeaza casetele
   * (altfel s-ar strica tastarea) - doar trimite valoarea curenta in sus, ca
   * parintele sa nu ramana cu o valoare veche daca se salveaza fara blur.
   */
  function emitLive(nextH, nextM) {
    if (!liveUpdate) return;
    const { total } = clampTotal(nextH, nextM);
    if (onChange) onChange(total);
    if (onCommit) onCommit(total);
  }

  function handleHourChange(e) {
    const next = e.target.value.replace(/\D/g, '').slice(0, 2);
    setHBoth(next);
    emitLive(next, mRef.current);
  }
  function handleMinuteChange(e) {
    const next = e.target.value.replace(/\D/g, '').slice(0, 2);
    setMBoth(next);
    emitLive(hRef.current, next);
  }
  function handleBlur() {
    editingRef.current = false;
    if (skipBlurCommitRef.current) {
      skipBlurCommitRef.current = false;
      return;
    }
    commit(hRef.current, mRef.current);
  }
  function handleFocus(e) {
    editingRef.current = true;
    // Resetare defensiva: daca un Escape anterior a ridicat steagul dar blur-ul
    // n-a mai ajuns la handler (modal inchis pe Escape, remount), steagul ar
    // inghiti tacut PRIMUL commit de dupa. Orice blur e precedat de un focus.
    skipBlurCommitRef.current = false;
    e.target.select();
  }
  function handleKeyDown(e, isHour) {
    // Enter -> commit fara a pierde focus
    if (e.key === 'Enter') {
      e.preventDefault();
      commit(hRef.current, mRef.current);
      e.target.blur();
      return;
    }
    // Tab dintr-o ora -> auto-commit hour-side
    if (e.key === 'Tab' && isHour) {
      // Lasa default tab (focus pe minute), commit la blur
    }
    // Esc -> revert (refacem din value)
    if (e.key === 'Escape') {
      e.preventDefault();
      const split = splitMinutes(value);
      setHBoth(String(split.h));
      setMBoth(String(split.m).padStart(2, '0'));
      // Commit EXPLICIT pe valoarea revenita: handleBlur ar folosi h/m din
      // closure-ul curent (cifrele tastate), deci Escape n-ar anula nimic.
      // In plus, cu liveUpdate am emis deja in timpul tastarii -> trebuie retras.
      editingRef.current = false;
      skipBlurCommitRef.current = true;
      commit(split.h, split.m);
      e.target.blur();
    }
  }

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        verticalAlign: 'middle',
        whiteSpace: 'nowrap',
      }}
      aria-label={ariaLabel}
    >
      <input
        type="text"
        inputMode="numeric"
        value={h}
        onChange={handleHourChange}
        onBlur={handleBlur}
        onFocus={handleFocus}
        onKeyDown={(e) => handleKeyDown(e, true)}
        disabled={disabled}
        style={inputStyle}
        aria-label="heures"
        maxLength={2}
      />
      <span style={sepStyle}>:</span>
      <input
        type="text"
        inputMode="numeric"
        value={m}
        onChange={handleMinuteChange}
        onBlur={handleBlur}
        onFocus={handleFocus}
        onKeyDown={(e) => handleKeyDown(e, false)}
        disabled={disabled}
        style={inputStyle}
        aria-label="minutes"
        maxLength={2}
      />
    </span>
  );
}
